function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import { Delaunay } from "d3-delaunay";
import { geoRotation, geoStereographic } from "d3-geo";
import { extent } from "d3-array";
import { asin, atan2, cos, degrees, max, min, radians, sign, sin, sqrt } from "./math.js";
import { cartesianNormalize as normalize, cartesianCross as cross, cartesianDot as dot, cartesianAdd } from "./cartesian.js"; // Converts 3D Cartesian to spherical coordinates (degrees).

function spherical(cartesian) {
  return [atan2(cartesian[1], cartesian[0]) * degrees, asin(max(-1, min(1, cartesian[2]))) * degrees];
} // Converts spherical coordinates (degrees) to 3D Cartesian.


function cartesian(coordinates) {
  var lambda = coordinates[0] * radians,
      phi = coordinates[1] * radians,
      cosphi = cos(phi);
  return [cosphi * cos(lambda), cosphi * sin(lambda), sin(phi)];
} // Spherical excess of a triangle (in spherical coordinates)


export function excess(triangle) {
  triangle = triangle.map(function (p) {
    return cartesian(p);
  });
  return dot(triangle[0], cross(triangle[2], triangle[1]));
}
export function geoDelaunay(points) {
  var delaunay = geo_delaunay_from(points),
      triangles = geo_triangles(delaunay),
      edges = geo_edges(triangles, points),
      neighbors = geo_neighbors(triangles, points.length),
      find = geo_find(neighbors, points),
      circumcenters = geo_circumcenters(triangles, points),
      _geo_polygons = geo_polygons(circumcenters, triangles, points),
      polygons = _geo_polygons.polygons,
      centers = _geo_polygons.centers,
      mesh = geo_mesh(polygons),
      hull = geo_hull(triangles, points),
      urquhart = geo_urquhart(edges, triangles);

  return {
    delaunay: delaunay,
    edges: edges,
    triangles: triangles,
    centers: centers,
    neighbors: neighbors,
    polygons: polygons,
    mesh: mesh,
    hull: hull,
    urquhart: urquhart,
    find: find
  };
}

function geo_find(neighbors, points) {
  function distance2(a, b) {
    var x = a[0] - b[0],
        y = a[1] - b[1],
        z = a[2] - b[2];
    return x * x + y * y + z * z;
  }

  return function find(x, y, next) {
    if (next === undefined) next = 0;
    var cell,
        dist,
        found = next;
    var xyz = cartesian([x, y]);

    do {
      cell = next;
      next = null;
      dist = distance2(xyz, cartesian(points[cell]));
      neighbors[cell].forEach(function (i) {
        var ndist = distance2(xyz, cartesian(points[i]));

        if (ndist < dist) {
          dist = ndist;
          next = i;
          found = i;
          return;
        }
      });
    } while (next !== null);

    return found;
  };
}

function geo_delaunay_from(points) {
  if (points.length < 2) return {}; // find a valid point to send to infinity

  var pivot = 0;

  while (isNaN(points[pivot][0] + points[pivot][1]) && pivot++ < points.length) {
    ;
  }

  var r = geoRotation(points[pivot]),
      projection = geoStereographic().translate([0, 0]).scale(1).rotate(r.invert([180, 0]));
  points = points.map(projection);
  var zeros = [];
  var max2 = 1;

  for (var i = 0, n = points.length; i < n; i++) {
    var m = Math.pow(points[i][0], 2) + Math.pow(points[i][1], 2);
    if (!isFinite(m) || m > 1e32) zeros.push(i);else if (m > max2) max2 = m;
  }

  var FAR = 1e6 * sqrt(max2);
  zeros.forEach(function (i) {
    return points[i] = [FAR, 0];
  }); // Add infinite horizon points

  points.push([0, FAR]);
  points.push([-FAR, 0]);
  points.push([0, -FAR]);
  var delaunay = Delaunay.from(points);
  delaunay.projection = projection; // clean up the triangulation

  var triangles = delaunay.triangles,
      halfedges = delaunay.halfedges,
      inedges = delaunay.inedges;
  var degenerate = [];

  for (var _i = 0, l = halfedges.length; _i < l; _i++) {
    if (halfedges[_i] < 0) {
      var j = _i % 3 == 2 ? _i - 2 : _i + 1;
      var k = _i % 3 == 0 ? _i + 2 : _i - 1;
      var a = halfedges[j];
      var b = halfedges[k];
      halfedges[a] = b;
      halfedges[b] = a;
      halfedges[j] = halfedges[k] = -1;
      triangles[_i] = triangles[j] = triangles[k] = pivot;
      inedges[triangles[a]] = a % 3 == 0 ? a + 2 : a - 1;
      inedges[triangles[b]] = b % 3 == 0 ? b + 2 : b - 1;
      degenerate.push(Math.min(_i, j, k));
      _i += 2 - _i % 3;
    } else if (triangles[_i] > points.length - 3 - 1) {
      triangles[_i] = pivot;
    }
  } // there should always be 4 degenerate triangles
  // console.warn(degenerate);


  return delaunay;
}

function geo_edges(triangles, points) {
  var _index = new Set();

  if (points.length === 2) return [[0, 1]];
  triangles.forEach(function (tri) {
    if (tri[0] === tri[1]) return;
    if (excess(tri.map(function (i) {
      return points[i];
    })) < 0) return;

    for (var i = 0, j; i < 3; i++) {
      j = (i + 1) % 3;

      _index.add(extent([tri[i], tri[j]]).join("-"));
    }
  });
  return Array.from(_index, function (d) {
    return d.split("-").map(Number);
  });
}

function geo_triangles(delaunay) {
  var triangles = delaunay.triangles;
  if (!triangles) return [];
  var geo_triangles = [];

  for (var i = 0, n = triangles.length / 3; i < n; i++) {
    var a = triangles[3 * i],
        b = triangles[3 * i + 1],
        c = triangles[3 * i + 2];

    if (a !== b && b !== c) {
      geo_triangles.push([a, c, b]);
    }
  }

  return geo_triangles;
}

function geo_circumcenters(triangles, points) {
  // if (!use_centroids) {
  return triangles.map(function (tri) {
    var c = tri.map(function (i) {
      return points[i];
    }).map(cartesian),
        V = cartesianAdd(cartesianAdd(cross(c[1], c[0]), cross(c[2], c[1])), cross(c[0], c[2]));
    return spherical(normalize(V));
  });
  /*} else {
    return triangles.map(tri => {
      return d3.geoCentroid({
        type: "MultiPoint",
        coordinates: tri.map(i => points[i])
      });
    });
  }*/
}

function geo_neighbors(triangles, npoints) {
  var neighbors = [];
  triangles.forEach(function (tri) {
    for (var j = 0; j < 3; j++) {
      var a = tri[j],
          b = tri[(j + 1) % 3];
      neighbors[a] = neighbors[a] || [];
      neighbors[a].push(b);
    }
  }); // degenerate cases

  if (triangles.length === 0) {
    if (npoints === 2) neighbors[0] = [1], neighbors[1] = [0];else if (npoints === 1) neighbors[0] = [];
  }

  return neighbors;
}

function geo_polygons(circumcenters, triangles, points) {
  var polygons = [];
  var centers = circumcenters.slice();

  if (triangles.length === 0) {
    if (points.length < 2) return {
      polygons: polygons,
      centers: centers
    };

    if (points.length === 2) {
      // two hemispheres
      var a = cartesian(points[0]),
          b = cartesian(points[1]),
          m = normalize(cartesianAdd(a, b)),
          d = normalize(cross(a, b)),
          c = cross(m, d);
      var poly = [m, cross(m, c), cross(cross(m, c), c), cross(cross(cross(m, c), c), c)].map(spherical).map(supplement);
      return polygons.push(poly), polygons.push(poly.slice().reverse()), {
        polygons: polygons,
        centers: centers
      };
    }
  }

  triangles.forEach(function (tri, t) {
    for (var j = 0; j < 3; j++) {
      var _a = tri[j],
          _b = tri[(j + 1) % 3],
          _c = tri[(j + 2) % 3];
      polygons[_a] = polygons[_a] || [];

      polygons[_a].push([_b, _c, t, [_a, _b, _c]]);
    }
  }); // reorder each polygon

  var reordered = polygons.map(function (poly) {
    var p = [poly[0][2]]; // t

    var k = poly[0][1]; // k = c

    for (var i = 1; i < poly.length; i++) {
      // look for b = k
      for (var j = 0; j < poly.length; j++) {
        if (poly[j][0] == k) {
          k = poly[j][1];
          p.push(poly[j][2]);
          break;
        }
      }
    }

    if (p.length > 2) {
      return p;
    } else if (p.length == 2) {
      var R0 = o_midpoint(points[poly[0][3][0]], points[poly[0][3][1]], centers[p[0]]),
          R1 = o_midpoint(points[poly[0][3][2]], points[poly[0][3][0]], centers[p[0]]);
      var i0 = supplement(R0),
          i1 = supplement(R1);
      return [p[0], i1, p[1], i0];
    }
  });

  function supplement(point) {
    var f = -1;
    centers.slice(triangles.length, Infinity).forEach(function (p, i) {
      if (p[0] === point[0] && p[1] === point[1]) f = i + triangles.length;
    });
    if (f < 0) f = centers.length, centers.push(point);
    return f;
  }

  return {
    polygons: reordered,
    centers: centers
  };
}

function o_midpoint(a, b, c) {
  a = cartesian(a);
  b = cartesian(b);
  c = cartesian(c);
  var s = sign(dot(cross(b, a), c));
  return spherical(normalize(cartesianAdd(a, b)).map(function (d) {
    return s * d;
  }));
}

function geo_mesh(polygons) {
  var mesh = [];
  polygons.forEach(function (poly) {
    if (!poly) return;
    var p = poly[poly.length - 1];

    var _iterator = _createForOfIteratorHelper(poly),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var q = _step.value;
        if (q > p) mesh.push([p, q]);
        p = q;
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  });
  return mesh;
}

function geo_urquhart(edges, triangles) {
  return function (distances) {
    var _lengths = new Map(),
        _urquhart = new Map();

    edges.forEach(function (edge, i) {
      var u = edge.join("-");

      _lengths.set(u, distances[i]);

      _urquhart.set(u, true);
    });
    triangles.forEach(function (tri) {
      var l = 0,
          remove = -1;

      for (var j = 0; j < 3; j++) {
        var u = extent([tri[j], tri[(j + 1) % 3]]).join("-");

        if (_lengths.get(u) > l) {
          l = _lengths.get(u);
          remove = u;
        }
      }

      _urquhart.set(remove, false);
    });
    return edges.map(function (edge) {
      return _urquhart.get(edge.join("-"));
    });
  };
}

function geo_hull(triangles, points) {
  var _hull = new Set(),
      hull = [];

  triangles.map(function (tri) {
    if (excess(tri.map(function (i) {
      return points[i > points.length ? 0 : i];
    })) > 1e-12) return;

    for (var i = 0; i < 3; i++) {
      var e = [tri[i], tri[(i + 1) % 3]],
          code = "".concat(e[0], "-").concat(e[1]);
      if (_hull.has(code)) _hull.delete(code);else _hull.add("".concat(e[1], "-").concat(e[0]));
    }
  });

  var _index = new Map();

  var start;

  _hull.forEach(function (e) {
    e = e.split("-").map(Number);

    _index.set(e[0], e[1]);

    start = e[0];
  });

  if (start === undefined) return hull;
  var next = start;

  do {
    hull.push(next);

    var n = _index.get(next);

    _index.set(next, -1);

    next = n;
  } while (next > -1 && next !== start);

  return hull;
}